import { useState, useEffect } from 'react';
import { api, getCurrentLocale } from '../services/api';

interface ImageModuleData {
  data: {
    id: number;
    attributes: {
      image: {
        data: {
          id: number;
          attributes: {
            url: string;
            alternativeText?: string;
          };
        };
      };
      altText: string;
    };
  };
}

interface ImageModuleListResponse {
  data: Array<{
    id: number;
    attributes: {
      title: string;
      altText: string;
      image: {
        data: {
          id: number;
          attributes: {
            url: string;
            alternativeText?: string;
          };
        } | null;
      };
    };
  }>;
}

export const useImageModule = (id?: number, title?: string) => {
  const [data, setData] = useState<ImageModuleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const locale = typeof window !== 'undefined' ? getCurrentLocale() : 'nl';
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (id) {
          // Try individual endpoint first
          try {
            const endpoint = `/image-modules/${id}?populate=image`;
            console.log('Fetching image module from:', endpoint);
            const response = await api.get(endpoint);
            console.log('Image module response:', response.data);
            setData(response.data);
            setError(null);
            return;
          } catch (individualErr: any) {
            console.log('Individual endpoint failed, trying list endpoint...');
          }
        }
        
        // Fallback to list endpoint and filter
        const endpoint = '/image-modules?populate=image';
        console.log('Fetching all image modules from:', endpoint);
        const response = await api.get(endpoint);
        console.log('All image modules response:', response.data);
        
        if (id || title) {
          // Find the specific module by ID or title
          const modules = response.data.data as Array<any>;
          console.log('All modules:', modules);
          
          let targetModule = null;
          if (id) {
            console.log('Looking for ID:', id);
            targetModule = modules.find(module => module.id === id);
          } else if (title) {
            console.log('Looking for title:', title);
            targetModule = modules.find(module => module.title === title);
          }
          
          console.log('Found target module:', targetModule);
          
          if (targetModule) {
            console.log('Target module:', targetModule);
            console.log('Target module image:', targetModule.image);
            
            setData({
              data: {
                id: targetModule.id,
                attributes: {
                  image: {
                    data: targetModule.image
                  },
                  altText: targetModule.altText
                }
              }
            });
            setError(null);
          } else {
            const searchCriteria = id ? `ID ${id}` : `title "${title}"`;
            setError(`Image module with ${searchCriteria} not found`);
          }
        } else {
          // Return first module if no ID or title specified
          const modules = response.data.data as Array<any>;
          if (modules.length > 0) {
            const firstModule = modules[0];
            console.log('First module:', firstModule);
            console.log('First module image:', firstModule.image);
            
            setData({
              data: {
                id: firstModule.id,
                attributes: {
                  image: {
                    data: firstModule.image
                  },
                  altText: firstModule.altText
                }
              }
            });
            setError(null);
          } else {
            setError('No image modules found');
          }
        }
      } catch (err: any) {
        console.error('Error fetching image module:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setError(err.message || 'Failed to fetch image module');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, title, typeof window !== 'undefined' ? getCurrentLocale() : 'nl']);

  return { data, loading, error };
}; 